import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../../components/ui/dialog";
import ExportButton from "../../components/ExportButton";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import { Plus, Search, Pencil, Trash2 } from "lucide-react";

const EMPTY = { client_name: "", client_code: "", pan: "", gstin: "", industry: "", contact_person: "", mobile: "", email: "", address: "", engagement_type: "Statutory Audit", status: "active" };

export default function Clients() {
  const { user } = useAuth();
  const canManage = ["partner","admin","manager"].includes(user?.role);
  const [list, setList] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/clients").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const openCreate = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (c) => { setForm({ ...EMPTY, ...c }); setEditId(c.id); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editId) {
        await api.patch(`/clients/${editId}`, form);
        toast.success("Client updated");
      } else {
        await api.post("/clients", form);
        toast.success("Client created");
      }
      setOpen(false); load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  const filtered = list.filter((c) =>
    !search || c.client_name?.toLowerCase().includes(search.toLowerCase()) || c.client_code?.toLowerCase().includes(search.toLowerCase())
  );

  const remove = async (id) => {
    if (!window.confirm("Permanently delete this client?")) return;
    try { await api.delete(`/clients/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="space-y-6 fade-in" data-testid="clients-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="eyebrow">Clients</div>
          <h1 className="font-sans text-4xl mt-2">Client master</h1>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
            <Input value={search} onChange={(e)=>setSearch(e.target.value)} placeholder="Search clients" className="pl-9 rounded-sm w-64" data-testid="client-search"/>
          </div>
          {canManage && <ExportButton path="/exports/clients" filename="clients.csv" testId="clients-export"/>}
          {canManage && <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={openCreate} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="add-client-btn"><Plus className="h-4 w-4 mr-2"/>Add Client</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader><DialogTitle className="font-sans text-2xl">{editId ? "Edit Client" : "New Client"}</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="grid grid-cols-2 gap-4">
                <div className="col-span-2"><Label className="eyebrow">Client Name</Label><Input required value={form.client_name} onChange={(e)=>setForm({...form, client_name: e.target.value})} className="mt-2 rounded-sm" data-testid="client-name-input"/></div>
                <div><Label className="eyebrow">Client Code</Label><Input value={form.client_code || ""} onChange={(e)=>setForm({...form, client_code: e.target.value})} placeholder="Auto-generated if empty" className="mt-2 rounded-sm font-mono" data-testid="client-code-input"/></div>
                <div><Label className="eyebrow">PAN</Label><Input value={form.pan} onChange={(e)=>setForm({...form, pan: e.target.value})} className="mt-2 rounded-sm"/></div>
                <div><Label className="eyebrow">GSTIN</Label><Input value={form.gstin} onChange={(e)=>setForm({...form, gstin: e.target.value})} className="mt-2 rounded-sm"/></div>
                <div><Label className="eyebrow">Industry</Label><Input value={form.industry} onChange={(e)=>setForm({...form, industry: e.target.value})} className="mt-2 rounded-sm"/></div>
                <div><Label className="eyebrow">Engagement Type</Label><Input value={form.engagement_type} onChange={(e)=>setForm({...form, engagement_type: e.target.value})} className="mt-2 rounded-sm"/></div>
                <div><Label className="eyebrow">Contact Person</Label><Input value={form.contact_person} onChange={(e)=>setForm({...form, contact_person: e.target.value})} className="mt-2 rounded-sm"/></div>
                <div><Label className="eyebrow">Mobile</Label><Input value={form.mobile} onChange={(e)=>setForm({...form, mobile: e.target.value})} className="mt-2 rounded-sm"/></div>
                <div className="col-span-2"><Label className="eyebrow">Email</Label><Input value={form.email} onChange={(e)=>setForm({...form, email: e.target.value})} className="mt-2 rounded-sm"/></div>
                <div className="col-span-2"><Label className="eyebrow">Address</Label><Input value={form.address} onChange={(e)=>setForm({...form, address: e.target.value})} className="mt-2 rounded-sm"/></div>
                <DialogFooter className="col-span-2"><Button type="submit" disabled={busy} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="client-save-btn">{busy ? "Saving..." : "Save"}</Button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 uppercase tracking-[0.18em] text-xs">
            <tr><th className="text-left p-4">Code</th><th className="text-left p-4">Name</th><th className="text-left p-4">PAN</th><th className="text-left p-4">GSTIN</th><th className="text-left p-4">Engagement</th><th className="text-left p-4">Status</th><th className="p-4"></th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-testid="clients-table">
            {filtered.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-slate-500">No clients yet.</td></tr>}
            {filtered.map((c) => (
              <tr key={c.id} className="hover:bg-slate-50">
                <td className="p-4 font-mono text-xs">{c.client_code}</td>
                <td className="p-4 font-medium"><Link to={`/portal/clients/${c.id}`} className="hover:text-[#946f30] transition-colors" data-testid={`client-view-${c.id}`}>{c.client_name}</Link></td>
                <td className="p-4 text-slate-600">{c.pan || "—"}</td>
                <td className="p-4 text-slate-600">{c.gstin || "—"}</td>
                <td className="p-4 text-slate-600">{c.engagement_type || "—"}</td>
                <td className="p-4"><span className="text-xs uppercase tracking-[0.2em] px-2 py-1 border border-emerald-200 text-emerald-700 bg-emerald-50 rounded-full">{c.status}</span></td>
                <td className="p-4 text-right">{canManage && <div className="inline-flex items-center gap-3"><button onClick={()=>openEdit(c)} className="text-slate-400 hover:text-slate-900" data-testid={`client-edit-${c.id}`}><Pencil className="h-4 w-4"/></button>{user?.role === "partner" && <button onClick={()=>remove(c.id)} className="text-slate-400 hover:text-red-600" data-testid={`client-delete-${c.id}`}><Trash2 className="h-4 w-4"/></button>}</div>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
